import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '@src/environments/environment';
import FroalaEditor from 'froala-editor';
import { catchError, exhaustMap, forkJoin, from, map, Observable, of } from 'rxjs';

import { AppService } from './app.service';
import { CUSTOM_EDITOR_BUTTONS_ACTIONS, CUSTOM_EDITOR_BUTTONS_TITLE, CUSTOM_MAT_ICONS_DEFINITIONS } from './common/enums/constants';
import { EditorActions } from './common/enums/enums';
import { NomWebServicesJson } from './common/services/nom-web-services-json';
import { getUrlPrefix } from './common/utils/util';

export const initApp = (): Observable<void> => {
    const httpClient = inject(HttpClient);
    const domSanitizer = inject(DomSanitizer);
    const nomWebServicesJson = inject(NomWebServicesJson);
    const registry = inject(MatIconRegistry);
    const appService = inject(AppService);
    const router = inject(Router);
    // alias name -> CSS class name
    registry.registerFontClassAlias('st-icons', 'st-icons');
    registerEditorMaterialIcons();

    return httpClient.get<Config>('./assets/config.json').pipe(
        map(data => {
            if (environment.production) {
                environment.user_id_space_replacement_character = data.user_id_space_replacement_character;
                environment.show_warnings = data.show_warnings;
                environment.show_errors = data.show_errors;
                environment.pst_version = data.pst_version;
                environment.pst_serial_number = data.pst_serial_number;
                environment.translated_label_login_error = data.translated_label_login_error;
                environment.translated_label_license_expired = data.translated_label_license_expired;
                environment.translated_label_ssogoogle = data.translated_label_ssogoogle;
                environment.translated_label_ssomicrosoft = data.translated_label_ssomicrosoft;
                environment.translated_label_ssomicrosoftlive = data.translated_label_ssomicrosoftlive;
                environment.service_address = data.service_address;
                environment.backup_service_address = data.backup_service_address;
                environment.discover_api_base_url = data.discover_api_base_url;
                environment.solr_poll_interval_seconds = data.solr_poll_interval_seconds;
                environment.default_domain = data.default_domain;
                environment.virtual_directory = data.virtual_directory;
                environment.demoFlag = data.demoFlag || false;
                environment.demoModeNotForProduction = data.demoModeNotForProduction || false;
                environment.read_rate_time_throttle_interval_seconds = data.read_rate_time_throttle_interval_seconds;
            }
            // Always override these.
            if (environment.demoModeNotForProduction) {
                environment.show_warnings = false;
                environment.show_errors = false;
            }
            window['storytellingConfig'] = environment;
            const urlPrefix = getUrlPrefix();
            CUSTOM_MAT_ICONS_DEFINITIONS.forEach(icon => {
                registry.addSvgIcon(icon.name, domSanitizer.bypassSecurityTrustResourceUrl(`${urlPrefix}/${icon.path}`));
            });
            nomWebServicesJson.initFromEnvironment();
        }),
        exhaustMap(() => {
            return from(appService.loadSessionData());
        }),
        exhaustMap(() => {
            return from(nomWebServicesJson.getActiveServiceAddress());
        }),
        exhaustMap(() => {
            return forkJoin([
                nomWebServicesJson.getObservable<SSOConfigSettings>('/api/SSOConfigSetings', { observe: 'response' }).pipe(map(r => r.body)),
                nomWebServicesJson.getObservable<SSOSettings[]>(`/api/SSOSettings/${navigator.language}`, { observe: 'response' }).pipe(map(r => r.body)),
            ]);
        }),
        map(([ssoConfigSettings, ssoSettings]) => {
            window.name = 'APStoryTelling';
            nomWebServicesJson.ssoConfigSettings.set(ssoConfigSettings);
            nomWebServicesJson.ssoProviders.set(ssoSettings || []);
            viewPortAdjustment();
            checkForSSO(router);
        }),
        catchError((err: unknown) => {
            console.warn(err);
            return of();
        })
    );
};

const viewPortAdjustment = () => {
    const viewport = document.getElementById('viewport');
    if (navigator.userAgent.match(/iPhone/i)) {
        viewport.setAttribute('content', 'viewport-fit=auto, width=device-width, height=device-height, initial-scale=1, maximum-scale=1, user-scalable=1, minimal-ui');
    } else {
        viewport.setAttribute('content', 'viewport-fit=cover, width=device-width, height=device-height, initial-scale=1, maximum-scale=1, user-scalable=1, minimal-ui');
    }
};

const checkForSSO = (router: Router) => {
    if (window.location.href !== null) {
        // if the URL is not null
        const hashQuery = window.location.hash.split('?')[1];
        const params = new URLSearchParams(hashQuery || '');
        const idToken = params.get('idToken');

        if (idToken) {
            localStorage.setItem('idToken', idToken);
            const providerName = params.get('providerName');
            if (providerName) {
                localStorage.setItem('providerName', providerName);
            }
            router.navigate(['/login'], { queryParams: {}, replaceUrl: true });
        }
    }
};

const registerEditorMaterialIcons = (): void => {
    const templateName = 'material_design';

    FroalaEditor.DefineIconTemplate(templateName, '<span class="material-symbols-outlined">[NAME]</span>');
    const additionalCommandsMap = {
        [EditorActions.COPY]: 'file_copy',
        [EditorActions.CUT]: 'content_cut',
        [EditorActions.PASTE]: 'content_paste',
    };
    const materialIconsMap = {
        bold: 'format_bold',
        italic: 'format_italic',
        underline: 'format_underlined',

        textColor: 'format_color_text',

        'align-left': 'format_align_left',
        'align-center': 'format_align_center',
        'align-right': 'format_align_right',

        undo: 'undo',
        redo: 'redo',
    };

    Object.keys(materialIconsMap).forEach(key => {
        FroalaEditor.DefineIcon(key, { NAME: materialIconsMap[key], template: templateName });
    });

    Object.keys(additionalCommandsMap).forEach(key => {
        const iconName = additionalCommandsMap[key];
        const command = CUSTOM_EDITOR_BUTTONS_ACTIONS[key] || key;
        const title = CUSTOM_EDITOR_BUTTONS_TITLE[key] || key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
        FroalaEditor.DefineIcon(iconName, { NAME: iconName, template: templateName });
        FroalaEditor.RegisterCommand(command, {
            title: title,
            icon: iconName,
            focus: true,
            undo: true,
            refreshAfterCallback: true,
            callback: async function () {
                this.events.trigger(command, [title]);
            },
        });
    });
};
